// Data-import client: upload + analyze a CSV/.xlsx file (XHR for upload %),
// re-analyze it with tweaked source options (no re-upload), then run — either
// generating INSERT text or starting a streaming background job whose progress
// is polled via lib/progress.js (shared with backup/restore).
import { api } from './api.js';
import { triggerDownload } from './export.js';

// uploadAnalyze POSTs the file as the raw request body (so xhr.upload gives
// byte-level progress) to an analyze endpoint and resolves to its JSON.
function uploadAnalyze(url, file, onUploadProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onUploadProgress) onUploadProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      let payload = null;
      try { payload = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload || {});
      else reject(new Error(payload?.error || `analyze failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.send(file);
  });
}

// analyzeImport (ad-hoc, table-scoped) → { token, analysis, targetColumns, autoMap }.
export function analyzeImport(connectionId, database, table, file, opt, onUploadProgress) {
  return uploadAnalyze(
    `/api/connections/${connectionId}/databases/${encodeURIComponent(database)}`
    + `/tables/${encodeURIComponent(table)}/import/analyze${sourceQuery(opt)}`,
    file, onUploadProgress);
}

// analyzeImportDef (saved-definition, grant-scoped) → { token, analysis }.
export function analyzeImportDef(defId, file, opt, onUploadProgress) {
  return uploadAnalyze(`/api/import-definitions/${defId}/analyze${sourceQuery(opt)}`, file, onUploadProgress);
}

// reanalyzeImport re-parses the already-uploaded file with new source options.
// → { analysis, autoMap }.
export async function reanalyzeImport(token, opt) {
  return api.post(`/api/import/${token}/analyze${sourceQuery(opt)}`, {});
}

// runImport either returns generated SQL (mode "generate" → { sql, rows,
// truncated }) or starts the executor (mode "execute" → { token, total }).
export async function runImport(token, body) {
  return api.post(`/api/import/${token}/run`, body);
}

// resolveImport answers a paused execute: "skip" | "skip-all" | "abort".
export async function resolveImport(token, action) {
  return api.post(`/api/import/${token}/resolve`, { action });
}

// downloadImportReport triggers the rejected-rows CSV download for a job token
// (download-attr trick so @solidjs/router doesn't swallow it — PROJECT_NOTES §5).
export function downloadImportReport(token) {
  triggerDownload(`/api/import/${token}/run-report`, 'import-errors.csv');
}

// getImportReport fetches the rejected rows as JSON for the in-app log viewer
// → { headers, rows: [{row, reason, values}], total, shown }.
export async function getImportReport(token) {
  return api.get(`/api/import/${token}/run-report?format=json`);
}

// getImportColumns loads a table's target columns WITHOUT a file — used when
// editing a saved definition (the sample file becomes optional). → { targetColumns }.
export async function getImportColumns(connectionId, database, table) {
  return api.get(`/api/connections/${connectionId}/databases/${encodeURIComponent(database)}/tables/${encodeURIComponent(table)}/import-columns`);
}

// sourceQuery serialises the parse options the analyze endpoints read from the
// query string (run sends them in the JSON body instead).
function sourceQuery(opt = {}) {
  const p = new URLSearchParams();
  if (opt.format) p.set('format', opt.format);
  p.set('hasHeader', opt.hasHeader === false ? 'false' : 'true');
  if (opt.delimiter) p.set('delimiter', opt.delimiter);
  if (opt.encoding) p.set('encoding', opt.encoding);
  if (opt.sheet) p.set('sheet', opt.sheet);
  const s = p.toString();
  return s ? `?${s}` : '';
}
